import { X, Check, Mail, FileText, User, Calendar, Phone, Users, AlignLeft } from 'lucide-react';
import React, { useState, useRef, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { addDoctor, checkLicenseExists } from '../../redux/doctorSlice';
import { checkEmailExists, checkMobileExists } from '../../redux/authSlice';
import { useTranslation } from 'react-i18next';
import Swal from "sweetalert2";
import { toast } from 'react-toastify';
import useAxiosAuth from '../../hooks/useAxiosAuth';
import GenderSelect from '../common/GenderSelect';
import { docPatCreationCountryList, getAllProfessionsList } from '../../redux/masterSlice';
import CountryTimeZoneSelect from '../common/CountryTimeZoneSelect';
import ProfessionSelect from '../common/ProfessionSelect';
import PhoneCountryDropdown from '../common/PhoneCountryDropdown';
import { getGenderOptions, formatFirstName, formatSurname } from '../../utils/personFields';
import { DOCTOR_NAME_MAX_LENGTH, DOCTOR_DESCRIPTION_MAX_LENGTH } from '../../utils/doctorFieldLimits';
import useSession from '../../hooks/useSession';
import { policyFor, policyErrorMessage, policyErrorField, missingAnyContact } from '../../utils/loginPolicy';

export default function AddDoctor({ isOpen, onClose, onSubmit }) {
    const dispatch = useDispatch();
    const { t, i18n } = useTranslation(['addDoctors', 'common']);
    const { licenseCheckLoading } = useSelector((state) => state.doctor);
    const { emailCheckLoading, mobileCheckLoading } = useSelector((state) => state.auth);
    const { countryTimezoneList, professionListAll, professionListAllLoading } = useSelector((state) => state.master);
    const axiosInstance = useAxiosAuth();

    // A tenant-bound admin creates doctors inside their own tenant, and the
    // backend stamps the tenant's country on them. So there's nothing to ask —
    // the field is shown read-only. super_admin (no tenant of their own) and
    // pre-multi-tenancy sessions still get the editable picker.
    const session = useSession();
    const inheritsTenantCountry = Boolean(session.isTenantBound && session.tenantCountryId);
    const tenantCountryName = (countryTimezoneList || [])
        .find((c) => c.countryId === session.tenantCountryId)?.countryName || '';
    // THE TENANT'S LOGIN POLICY (/tenants/public) decides which contacts are
    // mandatory — the e-mail when `email.required`, the mobile when
    // `mobile.required`, at least one under 'either' (anyOne). Nothing is
    // e-mail-only any more (owner, 1 Oct 2026, "Concept 2"): under "Mobile no
    // only" a doctor is created with a number and no e-mail. No OTP at
    // creation — the code would go to the doctor's phone, not the admin's. The
    // server sends the login id(s) + a temporary password; the doctor proves
    // each contact with a code at the FIRST sign-in (the activation door).
    const [publicTenants, setPublicTenants] = useState([]);
    useEffect(() => {
        if (!isOpen) return;
        let alive = true;
        (async () => {
            try {
                const res = await axiosInstance.get('/tenants/public');
                if (alive) setPublicTenants(res.data?.tenants || []);
            } catch {
                if (alive) setPublicTenants([]);
            }
        })();
        return () => { alive = false; };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isOpen]);
    const loginPolicy = policyFor(publicTenants, session.tenantId || '');
    const [formData, setFormData] = useState({
        name: '',
        surname: '',
        gender: '',
        dob: '',
        email: '',
        country: '',
        countryId: '',
        timeZone: '',
        phone: '',
        altPhone: '',
        licenseNumber: '',
        description: '',
        profession: [],
    });
    const selectedCountry = countryTimezoneList?.find(c => c.countryId === formData.countryId) || null;

    useEffect(() => {
        dispatch(docPatCreationCountryList());
        dispatch(getAllProfessionsList());
    }, [dispatch]);

    // Seed the inherited country into formData once the country list arrives.
    // The phone field validates its length against the selected country, so it
    // still needs a countryId even though the user never picks one. Runs only
    // while the field is empty, so it can't fight a user selection in the
    // editable (super_admin) case.
    useEffect(() => {
        if (!inheritsTenantCountry || formData.countryId) return;
        const c = (countryTimezoneList || []).find((x) => x.countryId === session.tenantCountryId);
        if (!c) return;
        setFormData((prev) => ({
            ...prev,
            countryId: c.countryId,
            country: c.countryName,
            timeZone: Array.isArray(c.timeZones) && c.timeZones.length ? c.timeZones[0] : prev.timeZone,
        }));
         
    }, [inheritsTenantCountry, countryTimezoneList, session.tenantCountryId, formData.countryId]);
    const [errors, setErrors] = useState({});
    const [apiError, setApiError] = useState(null);
    const [loading, setLoading] = useState(false);
    const [charLimitReached, setCharLimitReached] = useState({});
    const [emailStatus, setEmailStatus] = useState(null); // null: not checked, true: available, false: exists
    const emailCheckTimeoutRef = useRef(null);
    const lastCheckedEmailRef = useRef(''); // Track last checked email
    const isEmailCheckInProgressRef = useRef(false); // Track if email check is in progress
    const [phoneStatus, setPhoneStatus] = useState(null); // null: not checked, true: available, false: exists
    const phoneCheckTimeoutRef = useRef(null);
    const lastCheckedPhoneRef = useRef(''); // Track last checked phone number
    const isPhoneCheckInProgressRef = useRef(false); // Track if phone check is in progress
    const [licenseStatus, setLicenseStatus] = useState(null); // null: not checked, true: available, false: exists
    const licenseCheckTimeoutRef = useRef(null);
    const lastCheckedLicenseRef = useRef(''); // Track last checked license number
    const isLicenseCheckInProgressRef = useRef(false); // Track if license check is in progress

    // Store dispatch and axiosInstance in refs to avoid dependency issues
    const dispatchRef = useRef(dispatch);
    const axiosInstanceRef = useRef(axiosInstance);

    // Update refs when values change
    useEffect(() => {
        dispatchRef.current = dispatch;
        axiosInstanceRef.current = axiosInstance;
    }, [dispatch, axiosInstance]);

    // Proper email regex: allows letters, numbers, dots, hyphens, underscores, and plus signs in local part
    const emailRegex = /^[a-zA-Z0-9._+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;

    // Debounced email check
    useEffect(() => {
        if (!isOpen) return; // Don't check if modal is closed

        // Clear previous timeout
        if (emailCheckTimeoutRef.current) {
            clearTimeout(emailCheckTimeoutRef.current);
        }

        const email = formData.email?.trim() || '';

        // If email is empty or invalid format, reset status
        if (!email || !emailRegex.test(email)) {
            setEmailStatus(null);
            lastCheckedEmailRef.current = '';
            return;
        }

        // If email hasn't changed or check is in progress, don't check again
        if (lastCheckedEmailRef.current === email || isEmailCheckInProgressRef.current) {
            return;
        }

        // Set new timeout for debounced check (500ms delay)
        emailCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedEmail = formData.email.trim();

            // Double check email is still valid and hasn't changed during debounce
            if (!trimmedEmail || !emailRegex.test(trimmedEmail) || lastCheckedEmailRef.current === trimmedEmail || isEmailCheckInProgressRef.current) {
                return;
            }

            // Mark check as in progress and update last checked email BEFORE making the API call
            isEmailCheckInProgressRef.current = true;
            lastCheckedEmailRef.current = trimmedEmail;

            try {
                const resultAction = await dispatchRef.current(checkEmailExists({
                    email: trimmedEmail,
                    axiosInstance: axiosInstanceRef.current
                }));

                // Only update status if the email hasn't changed during the API call
                if (formData.email.trim() === trimmedEmail) {
                    if (checkEmailExists.fulfilled.match(resultAction)) {
                        // exists: true means email already exists (not available)
                        // exists: false means email doesn't exist (available)
                        setEmailStatus(!resultAction.payload.exists);
                    } else {
                        setEmailStatus(null);
                        lastCheckedEmailRef.current = ''; // Reset on error
                    }
                }
            } catch (error) {
                console.error('Error checking email:', error);
                if (formData.email.trim() === trimmedEmail) {
                    setEmailStatus(null);
                    lastCheckedEmailRef.current = ''; // Reset on error
                }
            } finally {
                isEmailCheckInProgressRef.current = false;
            }
        }, 500);

        // Cleanup function
        return () => {
            if (emailCheckTimeoutRef.current) {
                clearTimeout(emailCheckTimeoutRef.current);
            }
        };
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [formData.email, isOpen]);

    // Debounced phone check (only for main phone, not altPhone)
    useEffect(() => {
        if (!isOpen) return;

        if (phoneCheckTimeoutRef.current) {
            clearTimeout(phoneCheckTimeoutRef.current);
        }

        const phone = formData.phone?.trim() || '';

        // Need a valid phone number before checking
        const isValidIndia = formData.country === 'India' && /^\d{10}$/.test(phone);
        const isValidFrance = formData.country === 'France' && /^\d{9}$/.test(phone);

        if (!phone || (!isValidIndia && !isValidFrance)) {
            setPhoneStatus(null);
            lastCheckedPhoneRef.current = '';
            return;
        }

        if (lastCheckedPhoneRef.current === phone || isPhoneCheckInProgressRef.current) {
            return;
        }

        phoneCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedPhone = formData.phone.trim();

            const isStillValidIndia = formData.country === 'India' && /^\d{10}$/.test(trimmedPhone);
            const isStillValidFrance = formData.country === 'France' && /^\d{9}$/.test(trimmedPhone);

            if (!trimmedPhone || (!isStillValidIndia && !isStillValidFrance) || lastCheckedPhoneRef.current === trimmedPhone || isPhoneCheckInProgressRef.current) {
                return;
            }

            isPhoneCheckInProgressRef.current = true;
            lastCheckedPhoneRef.current = trimmedPhone;

            try {
                const resultAction = await dispatchRef.current(checkMobileExists({
                    phone: trimmedPhone,
                    axiosInstance: axiosInstanceRef.current
                }));

                if (formData.phone.trim() === trimmedPhone) {
                    if (checkMobileExists.fulfilled.match(resultAction)) {
                        setPhoneStatus(!resultAction.payload.exists);
                    } else {
                        setPhoneStatus(null);
                        lastCheckedPhoneRef.current = '';
                    }
                }
            } catch (error) {
                console.error('Error checking phone:', error);
                if (formData.phone.trim() === trimmedPhone) {
                    setPhoneStatus(null);
                    lastCheckedPhoneRef.current = '';
                }
            } finally {
                isPhoneCheckInProgressRef.current = false;
            }
        }, 500);

        return () => {
            if (phoneCheckTimeoutRef.current) {
                clearTimeout(phoneCheckTimeoutRef.current);
            }
        };
         
    }, [formData.phone, formData.country, isOpen]);

    // Debounced license check
    useEffect(() => {
        if (!isOpen) return; // Don't check if modal is closed

        // Clear previous timeout
        if (licenseCheckTimeoutRef.current) {
            clearTimeout(licenseCheckTimeoutRef.current);
        }

        const licenseNumber = formData.licenseNumber?.trim() || '';

        // If license number is empty, reset status
        if (!licenseNumber) {
            setLicenseStatus(null);
            lastCheckedLicenseRef.current = '';
            return;
        }

        // If license number hasn't changed or check is in progress, don't check again
        if (lastCheckedLicenseRef.current === licenseNumber || isLicenseCheckInProgressRef.current) {
            return;
        }

        // Set new timeout for debounced check (500ms delay)
        licenseCheckTimeoutRef.current = setTimeout(async () => {
            const trimmedLicense = formData.licenseNumber.trim();

            // Double check license is still valid and hasn't changed during debounce
            if (!trimmedLicense || lastCheckedLicenseRef.current === trimmedLicense || isLicenseCheckInProgressRef.current) {
                return;
            }

            // Mark check as in progress and update last checked license BEFORE making the API call
            isLicenseCheckInProgressRef.current = true;
            lastCheckedLicenseRef.current = trimmedLicense;

            try {
                const resultAction = await dispatchRef.current(checkLicenseExists({
                    licenseNumber: trimmedLicense,
                    axiosInstance: axiosInstanceRef.current
                }));

                // Only update status if the license hasn't changed during the API call
                if (formData.licenseNumber.trim() === trimmedLicense) {
                    if (checkLicenseExists.fulfilled.match(resultAction)) {
                        // exists: true means license already exists (not available)
                        // exists: false means license doesn't exist (available)
                        setLicenseStatus(!resultAction.payload.exists);
                    } else {
                        setLicenseStatus(null);
                        lastCheckedLicenseRef.current = ''; // Reset on error
                    }
                }
            } catch (error) {
                console.error('Error checking license:', error);
                if (formData.licenseNumber.trim() === trimmedLicense) {
                    setLicenseStatus(null);
                    lastCheckedLicenseRef.current = ''; // Reset on error
                }
            } finally {
                isLicenseCheckInProgressRef.current = false;
            }
        }, 500);

        // Cleanup function
        return () => {
            if (licenseCheckTimeoutRef.current) {
                clearTimeout(licenseCheckTimeoutRef.current);
            }
        };
         
    }, [formData.licenseNumber, isOpen]);


    const resetForm = () => {
        setFormData({
            name: '',
            surname: '',
            gender: '',
            dob: '',
            email: '',
            country: '',
            countryId: '',
            timeZone: '',
            phone: '',
            altPhone: '',
            licenseNumber: '',
            description: '',
            profession: [],
        });
        setErrors({});
        setApiError(null);
        setCharLimitReached({});
        setEmailStatus(null);
        setPhoneStatus(null);
        setLicenseStatus(null);
        lastCheckedEmailRef.current = '';
        lastCheckedPhoneRef.current = '';
        lastCheckedLicenseRef.current = '';
        isEmailCheckInProgressRef.current = false;
        isPhoneCheckInProgressRef.current = false;
        isLicenseCheckInProgressRef.current = false;
    };

    const calculateAge = (dob) => {
        const birthDate = new Date(dob);
        const today = new Date();
        // Use UTC getters for birthDate to avoid timezone shift on DOB
        let age = today.getFullYear() - birthDate.getUTCFullYear();
        const monthDiff = today.getMonth() - birthDate.getUTCMonth();
        if (monthDiff < 0 || (monthDiff === 0 && today.getDate() < birthDate.getUTCDate())) {
            age--;
        }
        return age;
    };

    const validateForm = () => {
        const newErrors = {};
        if (!formData.name.trim()) {
            newErrors.name = t('addDoctors.nameisrequired');
        } else if (/^\s+$/.test(formData.name)) {
            newErrors.name = t('addDoctors.name_only_spaces');
        } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(formData.name.trim())) {
            newErrors.name = t('addDoctors.name_invalid_chars');
        } else if (formData.name.trim().length < 2) {
            newErrors.name = t('addDoctors.name_min_length');
        }

        if (!formData.surname.trim()) {
            newErrors.surname = t('addDoctors.surnameisrequired');
        } else if (/^\s+$/.test(formData.surname)) {
            newErrors.surname = t('addDoctors.surname_only_spaces');
        } else if (!/^[a-zA-ZÀ-ÿ\s\-']+$/.test(formData.surname.trim())) {
            newErrors.surname = t('addDoctors.surname_invalid_chars');
        } else if (formData.surname.trim().length < 2) {
            newErrors.surname = t('addDoctors.surname_min_length');
        }

        if (!formData.gender) newErrors.gender = t('addDoctors.genderisrequired');
        if (!formData.dob) {
            newErrors.dob = t('addDoctors.dobisrequired');
        } else if (new Date(formData.dob) > new Date()) {
            newErrors.dob = t('addDoctors.futuredob');
        } else if (calculateAge(formData.dob) < 18) {
            newErrors.dob = t('addDoctors.18plus');
        } else if (new Date(formData.dob).getFullYear() < 1900) {
            newErrors.dob = t('addDoctors.dob_must_be_after_1900');
        }
        // Mandatory only when this tenant's login policy says so.
        if (!formData.email.trim()) {
            if (loginPolicy.email.required) newErrors.email = t('addDoctors.emailisrequired');
        } else {
            const email = formData.email.trim();
            // Check total length (RFC 5321: max 254 characters)
            if (email.length > 254) {
                newErrors.email = t('addDoctors.email_too_long');
            } else {
                // Split email into local and domain parts
                const parts = email.split('@');
                if (parts.length !== 2) {
                    newErrors.email = t('addDoctors.invalidemailformat');
                } else {
                    const [localPart, domainPart] = parts;
                    // Local part (before @) should be max 64 characters (RFC 5321)
                    if (localPart.length > 64) {
                        newErrors.email = t('validation.email_local_part_too_long', { ns: 'common' });
                    } else if (localPart.length === 0) {
                        newErrors.email = t('addDoctors.invalidemailformat');
                    } else if (domainPart.length === 0 || !domainPart.includes('.')) {
                        newErrors.email = t('addDoctors.invalidemailformat');
                    } else if (domainPart.length > 253) {
                        newErrors.email = t('validation.email_domain_part_too_long', { ns: 'common' });
                    } else if (!emailRegex.test(email)) {
                        newErrors.email = t('addDoctors.invalidemailformat');
                    } else if (emailStatus === false) {
                        newErrors.email = t('addDoctors.email_already_exists');
                    } else if (emailStatus === null && !emailCheckLoading) {
                        newErrors.email = t('addDoctors.verify_email_address');
                    }
                }
            }
        }
        // When the country is inherited from the tenant the user has no picker,
        // so requiring it here would be an un-clearable error.
        if (!inheritsTenantCountry && !formData.countryId) newErrors.countryId = t('addDoctors.countryisrequired');
        // Mandatory only when this hospital's login policy says so.
        if (!formData.phone.trim()) {
            if (loginPolicy.mobile.required) newErrors.phone = t('addDoctors.phonenumberisrequired');
        }
        else {
            const phoneMin = selectedCountry?.mobileMinLength || 10;
            const phoneMax = selectedCountry?.mobileMaxLength || 10;
            const phoneRegex = new RegExp(`^\\+?\\d{${phoneMin},${phoneMax}}$`);
            if (!phoneRegex.test(formData.phone)) {
                newErrors.phone = t('addDoctors.invalid_phone_number', { defaultValue: `Phone number must be between ${phoneMin} and ${phoneMax} digits.`, length: phoneMin === phoneMax ? phoneMin : `${phoneMin}-${phoneMax}` });
            } else if (phoneStatus === false) {
                newErrors.phone = t('addDoctors.phone_already_exists');
            } else if (phoneStatus === null && !mobileCheckLoading) {
                newErrors.phone = t('addDoctors.verify_phone_number');
            }
        }

        // 'either': neither field is starred, but one of them must be given.
        if (missingAnyContact(loginPolicy, { hasEmail: !!formData.email.trim(), hasPhone: !!formData.phone.trim() })) {
            newErrors.email = t('loginPolicy.one_contact_required', 'An e-mail or a mobile number is required');
        }

        if (formData.altPhone && formData.altPhone.trim()) {
            const altMin = selectedCountry?.altMinLength || selectedCountry?.mobileMinLength || 10;
            const altMax = selectedCountry?.altMaxLength || selectedCountry?.mobileMaxLength || 10;
            const altPhoneRegex = new RegExp(`^\\+?\\d{${altMin},${altMax}}$`);
            if (!altPhoneRegex.test(formData.altPhone)) {
                newErrors.altPhone = t('addDoctors.invalid_alt_phone_number', { defaultValue: `Alt phone number must be between ${altMin} and ${altMax} digits.`, length: altMin === altMax ? altMin : `${altMin}-${altMax}` });
            }
        }

        if (!formData.profession || formData.profession.length === 0) {
            newErrors.profession = t('addDoctors.profession_required', 'Please select at least 1 profession');
        } else if (formData.profession.length > 3) {
            newErrors.profession = t('addDoctors.profession_max_exceeded', 'Maximum 3 professions allowed');
        }

        if (formData.licenseNumber.trim()) {
            if (formData.licenseNumber.trim().length < 3) {
                newErrors.licenseNumber = t('addDoctors.license_min_length');
            } else if (formData.licenseNumber.trim().length > 20) {
                newErrors.licenseNumber = t('addDoctors.license_max_length');
            } else if (!/^[a-zA-Z0-9]+$/.test(formData.licenseNumber.trim())) {
                newErrors.licenseNumber = t('addDoctors.license_invalid_chars');
            } else if (licenseStatus === false) {
                newErrors.licenseNumber = t('addDoctors.license_already_exists');
            } else if (licenseStatus === null && !licenseCheckLoading) {
                newErrors.licenseNumber = t('addDoctors.verify_license_number');
            }
        }

        // Description validation (optional field, but if provided, validate)
        if (formData.description && formData.description.trim()) {
            // Sanitize description: remove potentially dangerous characters
            const sanitized = formData.description.replace(/[<>]/g, '');
            if (sanitized !== formData.description) {
                // Update formData with sanitized value
                setFormData(prev => ({ ...prev, description: sanitized }));
            }
            if (formData.description.length > DOCTOR_DESCRIPTION_MAX_LENGTH) {
                newErrors.description = t('doctorFields.description_max', { defaultValue: 'Description cannot exceed {{max}} characters.', max: DOCTOR_DESCRIPTION_MAX_LENGTH });
            } else if (/^\s+$/.test(formData.description)) {
                newErrors.description = t('addDoctors.description_only_spaces');
            }
        }

        setErrors(newErrors);
        return Object.keys(newErrors).length === 0;
    };

    const handleChange = (e) => {
        const { name, value } = e.target;
        const maxLengths = {
            name: DOCTOR_NAME_MAX_LENGTH,
            surname: DOCTOR_NAME_MAX_LENGTH,
            phone: selectedCountry?.mobileMaxLength || 15,
            altPhone: selectedCountry?.altMaxLength || selectedCountry?.mobileMaxLength || 15,
            licenseNumber: 20
        };
        let processedValue = value;

        // First name: only the first character uppercased.
        if (name === 'name' && processedValue.length > 0) {
            processedValue = formatFirstName(processedValue);
        }
        // Surname (family name): first character uppercased, rest as typed.
        if (name === 'surname' && processedValue.length > 0) {
            processedValue = formatSurname(processedValue);
        }

        // Restrict phone and altPhone to digits only
        if ((name === "phone" || name === "altPhone") && processedValue && !/^\d*$/.test(processedValue)) return;

        // Restrict name and surname to letters, spaces, hyphens, and apostrophes only
        if ((name === "name" || name === "surname") && processedValue && !/^[a-zA-ZÀ-ÿ\s\-']*$/.test(processedValue)) return;

        // Restrict email to valid email characters only (letters, numbers, dots, hyphens, underscores, plus, @)
        if (name === "email" && value) {
            // Allow only valid email characters: letters, numbers, dots, hyphens, underscores, plus, and @
            if (!/^[a-zA-Z0-9._+-@]*$/.test(value)) return;
        }

        // Restrict licenseNumber to alphanumeric only (no special characters)
        if (name === "licenseNumber" && value && !/^[a-zA-Z0-9]*$/.test(value)) return;

        // Sanitize description: remove potentially dangerous characters
        if (name === "description" && value) {
            const sanitized = value.replace(/[<>]/g, '');
            if (sanitized !== value) {
                // Update with sanitized value
                setFormData((prev) => ({
                    ...prev,
                    [name]: sanitized,
                }));
                setApiError(null);
                return;
            }
        }

        // Check if character limit is reached
        if (maxLengths[name] && value.length >= maxLengths[name]) {
            setCharLimitReached({ ...charLimitReached, [name]: true });
        } else {
            setCharLimitReached({ ...charLimitReached, [name]: false });
        }

        // Reset email status and tracking when email changes
        if (name === 'email') {
            setEmailStatus(null);
            lastCheckedEmailRef.current = '';
            isEmailCheckInProgressRef.current = false;
        }

        // Reset phone status and tracking when phone changes
        if (name === 'phone') {
            setPhoneStatus(null);
            lastCheckedPhoneRef.current = '';
            isPhoneCheckInProgressRef.current = false;
        }

        // Reset license status and tracking when license number changes
        if (name === 'licenseNumber') {
            setLicenseStatus(null);
            lastCheckedLicenseRef.current = '';
            isLicenseCheckInProgressRef.current = false;
        }

        setFormData((prev) => ({
            ...prev,
            [name]: processedValue,
            ...(name === 'dob' && processedValue ? { age: calculateAge(processedValue) } : {}),
        }));
        setApiError(null);

        // Clear error for this field when user starts typing, or apply live validation for dob
        if (name === 'dob') {
            let dobError = '';
            if (!processedValue) {
                dobError = t('addDoctors.dobisrequired');
            } else if (new Date(processedValue) > new Date()) {
                dobError = t('addDoctors.futuredob');
            } else if (calculateAge(processedValue) < 18) {
                dobError = t('addDoctors.18plus');
            } else if (new Date(processedValue).getFullYear() < 1900) {
                dobError = t('addDoctors.dob_must_be_after_1900');
            }

            setErrors((prev) => ({ ...prev, dob: dobError }));
        } else if (errors[name]) {
            setErrors((prev) => ({ ...prev, [name]: '' }));
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (!validateForm()) return;

        // A FILLED identifier must pass the duplicate check. No OTP here —
        // the doctor proves ownership at the first sign-in.
        if (formData.email.trim() && emailStatus !== true) {
            toast.warn(t('addDoctors.verify_email_to_proceed', 'Please verify your email address. It must be available to proceed.'));
            return;
        }
        if (formData.phone.trim() && phoneStatus !== true) {
            toast.warn(t('addDoctors.verify_phone_to_proceed'));
            return;
        }

        // Ensure license is validated before submission if provided
        if (formData.licenseNumber.trim() && licenseStatus !== true) {
            toast.warn(t('addDoctors.verify_license_to_proceed', 'Please verify your license number. It must be available to proceed.'));
            return;
        }

        const result = await Swal.fire({
            title: t('addDoctors.confirmTitle'),
            text: t('addDoctors.confirmText'),
            icon: 'question',
            showCancelButton: true,
            confirmButtonText: t('addDoctors.confirmButtonText', 'Yes, Add'),
            cancelButtonText: t('addDoctors.cancelButtonText', 'Cancel'),
            confirmButtonColor: '#4ABDBD',
            cancelButtonColor: '#e5e7eb',
        });

        if (!result.isConfirmed) {
            return;
        }

        setLoading(true);
        setApiError(null);

        try {
            const doctorRes = await dispatch(
                addDoctor({
                    formData: {
                        name: formData.name,
                        surname: formData.surname,
                        gender: formData.gender,
                        dob: formData.dob,
                        email: formData.email.trim() || undefined,
                        phone: formData.phone,
                        altPhone: formData.altPhone,
                        licenseNumber: formData.licenseNumber,
                        description: formData.description,
                        countryId: formData.countryId,
                        timeZone: formData.timeZone,
                        profession: formData.profession.length > 0 ? formData.profession : undefined,
                        profileStatus: 'complete',
                    }
                })
            ).unwrap();

            if (!doctorRes || !doctorRes.doctor || !doctorRes.doctor.doctorId || doctorRes.success === false) {
                throw new Error(
                    doctorRes?.error?.message?.en ||
                    doctorRes?.error?.message ||
                    t('addDoctors.doccreationfailed')
                );
            }

            toast.success(t('addDoctors.successText'));

            onSubmit({
                success: true,
                doctor: doctorRes,
            });

            resetForm();
            onClose();

        } catch (error) {
            console.log('error', error);
            // Login-policy 400s (FIELD_REQUIRED) — field-level, translated.
            const policyData = error?.error?.response?.data;
            const policyMsg = policyErrorMessage(policyData, t, i18n.language);
            if (policyMsg) {
                setErrors(prev => ({ ...prev, [policyErrorField(policyData.field)]: policyMsg }));
                setApiError(policyMsg);
                toast.warn(policyMsg);
                return;
            }
            const errMsg =
                error.error?.response?.data?.message?.en ||
                error.message ||
                t('addDoctors.erroroccured');

            setApiError(errMsg);

            toast.warn(errMsg);
        } finally {
            setLoading(false);
        }
    };

    const handleClose = () => {
        resetForm();
        onClose();
    };

    if (!isOpen) return null;

    return (
        <div data-testid="add-doctor-modal" className="fixed inset-0 bg-black/50 flex items-center justify-center z-50 p-2 sm:p-4">
            <div data-testid="add-doctor-modal-content" className="bg-white my-5 rounded-2xl shadow-2xl w-full max-w-5xl max-h-[80vh] sm:max-h-[90vh] flex flex-col">
                {/* Header Section - Sticky */}
                <div data-testid="add-doctor-modal-header" className="p-4 sm:p-6 sticky top-0 bg-gradient-to-r from-[#4ABDBD]/5 to-[#3AA8A8]/5 z-10 flex justify-between items-center border-b border-gray-200">
                    <h3 data-testid="add-doctor-modal-title" className="text-base sm:text-lg md:text-xl font-bold bg-gradient-to-r from-[#4ABDBD] to-[#3AA8A8] bg-clip-text text-transparent">{t('addDoctors.heading')}</h3>
                    <button
                        data-testid="close-add-doctor-modal"
                        onClick={handleClose}
                        className="group/btn text-gray-500 hover:text-white p-2 rounded-xl bg-gradient-to-r from-gray-100 to-gray-200 hover:from-gray-400 hover:to-gray-500 shadow-md hover:shadow-lg cursor-pointer transition-all duration-300 hover:scale-110 relative overflow-hidden"
                    >
                        <X data-testid="close-modal-icon" className="h-5 w-5 sm:h-6 sm:w-6" />
                    </button>
                </div>

                {/* Scrollable Form Content */}
                <div className="flex-1 overflow-y-auto px-4 sm:px-6 py-4 custom-scrollbar">
                    {apiError && (
                        <div data-testid="add-doctor-api-error" className="p-4 bg-red-50 text-red-700 border border-red-200 rounded-xl mb-4 shadow-md">
                            {apiError}
                        </div>
                    )}
                    <form data-testid="add-doctor-form" onSubmit={handleSubmit} className={`grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 ${loading ? "opacity-50 pointer-events-none" : ""}`}>
                        {/* 1. Gender */}
                        <div data-testid="gender-field-container">
                            <label htmlFor="gender" data-testid="gender-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.gender')} <span className="text-red-500">*</span></label>
                            <GenderSelect
                                value={formData.gender}
                                onChange={(val) => {
                                    setFormData(prev => ({ ...prev, gender: val }));
                                    setErrors(prev => ({ ...prev, gender: null }));
                                }}
                                options={getGenderOptions(t)}
                                placeholder={t('addDoctors.selectgender', 'Select Gender')}
                                error={!!errors.gender}
                            />
                            {errors.gender && <p data-testid="gender-error" className="mt-2 text-xs text-red-500 font-medium">{errors.gender}</p>}
                        </div>
                        {/* 2. First Name */}
                        <div data-testid="name-field-container">
                            <label htmlFor="name" data-testid="name-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.name')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.name ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <User className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="name-input"
                                    name="name"
                                    type="text"
                                    value={formData.name}
                                    onChange={handleChange}
                                    placeholder={t('addDoctors.name_placeholder', "Enter doctor's first name")}
                                    maxLength={DOCTOR_NAME_MAX_LENGTH}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.name && <p data-testid="name-error" className="mt-2 text-xs text-red-500 font-medium">{errors.name}</p>}
                            {charLimitReached.name && <p className="mt-2 text-xs text-red-500 font-medium">{t('addDoctors.reached_text_limit', { limit: DOCTOR_NAME_MAX_LENGTH })}</p>}
                        </div>
                        {/* 3. Surname */}
                        <div data-testid="surname-field-container">
                            <label htmlFor="surname" data-testid="surname-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.surname')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.surname ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <User className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="surname-input"
                                    name="surname"
                                    type="text"
                                    value={formData.surname}
                                    onChange={handleChange}
                                    placeholder={t('addDoctors.surname_placeholder', "Enter doctor's last name")}
                                    maxLength={DOCTOR_NAME_MAX_LENGTH}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.surname && <p data-testid="surname-error" className="mt-2 text-xs text-red-500 font-medium">{errors.surname}</p>}
                            {charLimitReached.surname && <p className="mt-2 text-xs text-red-500 font-medium">{t('addDoctors.reached_text_limit', { limit: DOCTOR_NAME_MAX_LENGTH })}</p>}
                        </div>
                        <div data-testid="dob-field-container">
                            <label htmlFor="dob" data-testid="dob-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.dob')} <span className="text-red-500">*</span></label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.dob ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Calendar className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="dob-input"
                                    name="dob"
                                    type="date"
                                    value={formData.dob}
                                    onChange={handleChange}
                                    min="1900-01-01"
                                    max={new Date().toISOString().split('T')[0]}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 text-sm"
                                />
                            </div>
                            {errors.dob && <p data-testid="dob-error" className="mt-2 text-xs text-red-500 font-medium">{errors.dob}</p>}
                        </div>
                        <div data-testid="age-field-container">
                            <label htmlFor="age" data-testid="age-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.age')}</label>
                            <div className="relative flex items-center border rounded-xl p-3 bg-gray-100 border-gray-300">
                                <Calendar className="text-gray-400 w-5 h-5 mr-2" />
                                <input
                                    data-testid="age-input"
                                    name="age"
                                    type="number"
                                    value={formData.dob && !errors.dob ? calculateAge(formData.dob) : ''}
                                    readOnly
                                    className="w-full bg-transparent focus:outline-none text-gray-800 text-sm cursor-not-allowed"
                                />
                            </div>
                        </div>
                        <div data-testid="email-field-container">
                            <label htmlFor="email" data-testid="email-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.email')} {loginPolicy.email.required && <span className="text-red-500">*</span>}</label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${emailStatus === false
                                    ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                                    : emailStatus === true
                                        ? 'border-teal-300 focus-within:ring-2 focus-within:ring-teal-500'
                                        : errors.email
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Mail className="text-[#4ABDBD] w-5 h-5 mr-2 flex-shrink-0" />
                                <input
                                    data-testid="email-input"
                                    name="email"
                                    type="email"
                                    value={formData.email}
                                    onChange={handleChange}
                                    placeholder={t('addDoctors.email_placeholder', 'Enter email address')}
                                    maxLength={254}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                                {emailCheckLoading && (
                                    <span className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin ml-2 flex-shrink-0"></span>
                                )}
                                {!emailCheckLoading && emailStatus === false && (
                                    <X
                                        className="text-red-500 w-5 h-5 ml-2 flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, email: '' }));
                                            setEmailStatus(null);
                                            setErrors(prev => ({ ...prev, email: '' }));
                                            lastCheckedEmailRef.current = '';
                                        }}
                                    />
                                )}
                                {!emailCheckLoading && emailStatus === true && (
                                    <Check className="text-green-500 w-5 h-5 ml-2 flex-shrink-0" />
                                )}
                            </div>
                            {errors.email && <p data-testid="email-error" className="mt-2 text-xs text-red-500 font-medium">{errors.email}</p>}
                            {!emailCheckLoading && emailStatus === false && (
                                <p className="text-red-500 text-xs mt-1">{t('addDoctors.email_already_exists')}</p>
                            )}
                            {/* OTP-mandated field: proved by the doctor at the first sign-in. */}
                            {loginPolicy.email.otp && (
                                <p data-testid="email-otp-hint" className="mt-1 text-xs text-gray-500">{t('loginPolicy.verified_at_first_sign_in', 'They will verify this with a one-time code at their first sign-in.')}</p>
                            )}
                        </div>
                        <div data-testid="phone-field-container">
                            <label
                                htmlFor="phone"
                                data-testid="phone-label"
                                className="block text-sm font-semibold text-gray-700 mb-2"
                            >
                                {t('addDoctors.phone')} {loginPolicy.mobile.required && <span className="text-red-500">*</span>}
                            </label>
                            <div className={`relative flex items-center border rounded-xl p-3 ${!formData.countryId ? 'bg-gray-100' : 'bg-white'} ${phoneStatus === false
                                    ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                                    : phoneStatus === true
                                        ? 'border-teal-300 focus-within:ring-2 focus-within:ring-teal-500'
                                        : errors.phone
                                            ? 'border-red-500 bg-red-50'
                                            : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Phone className={`w-5 h-5 mr-2 flex-shrink-0 ${!formData.countryId ? 'text-gray-400' : 'text-[#4ABDBD]'}`} />
                                <PhoneCountryDropdown
                                    countryTimezoneList={countryTimezoneList}
                                    countryId={formData.countryId}
                                    onSelect={({ countryId, timeZone, countryName }) => {
                                        setFormData(prev => ({ ...prev, countryId, timeZone, country: countryName, phone: '', altPhone: '' }));
                                        setErrors(prev => ({ ...prev, countryId: null, timeZone: null }));
                                        setPhoneStatus(null);
                                        lastCheckedPhoneRef.current = '';
                                    }}
                                />
                                <input
                                    data-testid="phone-input"
                                    name="phone"
                                    type="tel"
                                    value={formData.phone}
                                    onChange={handleChange}
                                    onKeyPress={(e) => { if (!/[0-9]/.test(e.key)) e.preventDefault(); }}
                                    placeholder={!formData.countryId ? t('addDoctors.select_country_first', 'Select country first') : t('addDoctors.phone_placeholder', 'Enter phone number')}
                                    maxLength={selectedCountry?.mobileMaxLength || 15}
                                    disabled={!formData.countryId}
                                    className={`w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm ${!formData.countryId ? 'cursor-not-allowed' : ''}`}
                                />
                                {mobileCheckLoading && (
                                    <span className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin ml-2 flex-shrink-0"></span>
                                )}
                                {!mobileCheckLoading && phoneStatus === false && (
                                    <X
                                        className="text-red-500 w-5 h-5 ml-2 flex-shrink-0 cursor-pointer hover:scale-110 transition-transform"
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, phone: '' }));
                                            setPhoneStatus(null);
                                            setErrors(prev => ({ ...prev, phone: '' }));
                                            lastCheckedPhoneRef.current = '';
                                        }}
                                    />
                                )}
                                {!mobileCheckLoading && phoneStatus === true && (
                                    <Check className="text-green-500 w-5 h-5 ml-2 flex-shrink-0" />
                                )}
                            </div>
                            {errors.phone && (
                                <p data-testid="phone-error" className="mt-2 text-xs text-red-500 font-medium">
                                    {errors.phone}
                                </p>
                            )}
                            {!mobileCheckLoading && phoneStatus === false && (
                                <p className="text-red-500 text-xs mt-1">{t('addDoctors.phone_already_exists')}</p>
                            )}
                            {/* OTP-mandated field: proved by the doctor at the first sign-in. */}
                            {loginPolicy.mobile.otp && (
                                <p data-testid="phone-otp-hint" className="mt-1 text-xs text-gray-500">{t('loginPolicy.verified_at_first_sign_in', 'They will verify this with a one-time code at their first sign-in.')}</p>
                            )}
                        </div>

                        <div data-testid="alt-phone-field-container">
                            <label
                                htmlFor="altPhone"
                                data-testid="alt-phone-label"
                                className="block text-sm font-semibold text-gray-700 mb-2"
                            >
                                {t('addDoctors.alt_phone', 'Alternate mobile no')}
                            </label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${errors.altPhone ? 'border-red-500 bg-red-50' : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <Phone className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="alt-phone-input"
                                    name="altPhone"
                                    type="text"
                                    value={formData.altPhone}
                                    onChange={handleChange}
                                    placeholder={formData.country === 'India' ? t('addDoctors.phone_placeholder_india', 'Enter 10-digit phone number') : formData.country === 'France' ? t('addDoctors.phone_placeholder_france', 'Enter 9-digit phone number') : t('addDoctors.phone_placeholder', 'Enter phone number')}
                                    maxLength={formData.country === 'India' ? 10 : 9}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm"
                                />
                            </div>
                            {errors.altPhone && (
                                <p data-testid="alt-phone-error" className="mt-2 text-xs text-red-500 font-medium">
                                    {errors.altPhone}
                                </p>
                            )}
                        </div>

                        {/* Profession Select */}
                        <div data-testid="profession-field-container">
                            <label data-testid="profession-label" className="block text-sm font-semibold text-gray-700 mb-2">
                                {t('addDoctors.profession', 'Profession')} <span className="text-red-500">*</span>
                            </label>
                            <ProfessionSelect
                                value={formData.profession}
                                onChange={(val) => {
                                    setFormData(prev => ({ ...prev, profession: val }));
                                    if (errors.profession) setErrors(prev => ({ ...prev, profession: '' }));
                                }}
                                professions={professionListAll || []}
                                loading={professionListAllLoading}
                                placeholder={t('addDoctors.select_profession', 'Select Profession')}
                                error={!!errors.profession}
                            />
                            {errors.profession && <p data-testid="profession-error" className="text-red-500 text-xs mt-1">{errors.profession}</p>}
                        </div>

                        <div data-testid="license-number-field-container">
                            <label htmlFor="licenseNumber" data-testid="license-number-label" className="block text-sm font-semibold text-gray-700 mb-2">
                                {t('addDoctors.licensenumber')}
                            </label>
                            <div className={`relative flex items-center border rounded-xl p-3 bg-white ${licenseStatus === false
                                ? 'border-red-300 focus-within:ring-2 focus-within:ring-red-500'
                                : licenseStatus === true
                                    ? 'border-green-300 focus-within:ring-2 focus-within:ring-green-500'
                                    : errors.licenseNumber
                                        ? 'border-red-500 bg-red-50'
                                        : 'border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]'
                                }`}>
                                <FileText className="text-[#4ABDBD] w-5 h-5 mr-2" />
                                <input
                                    data-testid="license-number-input"
                                    name="licenseNumber"
                                    type="text"
                                    value={formData.licenseNumber}
                                    onChange={handleChange}
                                    placeholder={t('addDoctors.license_placeholder', 'Enter license number')}
                                    maxLength={20}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm pr-8"
                                />
                                {licenseCheckLoading && (
                                    <span className="w-4 h-4 border-2 border-[#4ABDBD] border-t-transparent rounded-full animate-spin ml-2"></span>
                                )}
                                {!licenseCheckLoading && licenseStatus === true && (
                                    <Check className="text-green-500 w-5 h-5 ml-2" />
                                )}
                                {!licenseCheckLoading && licenseStatus === false && (
                                    <X
                                        className="text-red-500 w-5 h-5 ml-2 cursor-pointer hover:scale-110 transition-transform"
                                        onClick={() => {
                                            setFormData(prev => ({ ...prev, licenseNumber: '' }));
                                            setLicenseStatus(null);
                                            setErrors(prev => ({ ...prev, licenseNumber: '' }));
                                            lastCheckedLicenseRef.current = '';
                                        }}
                                    />
                                )}
                            </div>
                            {errors.licenseNumber && <p data-testid="license-number-error" className="mt-2 text-xs text-red-500 font-medium">{errors.licenseNumber}</p>}
                            {charLimitReached.licenseNumber && <p className="mt-2 text-xs text-red-500 font-medium">{t('addDoctors.reached_text_limit', { limit: 20 })}</p>}
                        </div>

                        <div data-testid="description-field-container" className="sm:col-span-2 lg:col-span-3">
                            <label htmlFor="description" data-testid="description-label" className="block text-sm font-semibold text-gray-700 mb-2">{t('addDoctors.description')}</label>
                            <div className="relative flex items-start border rounded-xl p-3 bg-white border-gray-300 focus-within:ring-2 focus-within:ring-[#4ABDBD] hover:border-[#4ABDBD]">
                                <AlignLeft className="text-[#4ABDBD] w-5 h-5 mr-2 mt-1 flex-shrink-0" />
                                <textarea
                                    data-testid="description-textarea"
                                    name="description"
                                    value={formData.description}
                                    onChange={handleChange}
                                    placeholder={t('addDoctors.description_placeholder', "Enter doctor's professional description (optional)")}
                                    className="w-full bg-transparent focus:outline-none text-gray-800 placeholder-gray-400 text-sm resize-none"
                                    rows="4"
                                    maxLength={DOCTOR_DESCRIPTION_MAX_LENGTH}
                                />
                            </div>
                            {errors.description && <p data-testid="description-error" className="mt-2 text-xs text-red-500 font-medium">{errors.description}</p>}
                        </div>
                        {/* Country. A doctor inherits their tenant's country, so for a
                            tenant-bound admin this is shown read-only rather than asked —
                            the backend derives it from the tenant regardless of what is
                            sent. The editable picker remains for super_admin and for
                            sessions with no tenant (pre-multi-tenancy tokens). */}
                        <div data-testid="country-field-container" className="sm:col-span-2 lg:col-span-3">
                            <label htmlFor="country" data-testid="country-label" className="block text-sm font-semibold text-gray-700 mb-2">
                                {t('addDoctors.country')} {!inheritsTenantCountry && <span className="text-red-500">*</span>}
                            </label>
                            {inheritsTenantCountry ? (
                                <>
                                    <div
                                        data-testid="country-inherited"
                                        className="w-full px-3 py-2.5 text-sm border border-gray-200 rounded-xl bg-gray-50 text-gray-600"
                                    >
                                        {tenantCountryName || '—'}
                                    </div>
                                    <p className="mt-1 text-[11px] text-gray-500">
                                        {t('addDoctors.country_from_tenant', "Inherited from your organisation — doctors use their tenant's country.")}
                                    </p>
                                </>
                            ) : (
                            <CountryTimeZoneSelect
                                countryTimezoneList={countryTimezoneList}
                                countryId={formData.countryId}
                                timeZone={formData.timeZone}
                                onChange={({ countryId, timeZone, countryName }) => {
                                    setFormData(prev => ({ ...prev, countryId, timeZone, country: countryName, phone: '', altPhone: '' }));
                                    setErrors(prev => ({ ...prev, countryId: null, timeZone: null }));
                                    setPhoneStatus(null);
                                    lastCheckedPhoneRef.current = '';
                                }}
                                placeholder={t('addDoctors.selectcountry', 'Select Country')}
                                error={!!errors.countryId || !!errors.timeZone}
                            />
                            )}
                            {!inheritsTenantCountry && errors.countryId && <p data-testid="country-error" className="mt-2 text-xs text-red-500 font-medium">{errors.countryId}</p>}
                            {!inheritsTenantCountry && errors.timeZone && <p className="mt-2 text-xs text-red-500 font-medium">{errors.timeZone}</p>}
                        </div>
                    </form>
                </div>

                {/* Footer Section - Sticky */}
                <div data-testid="form-actions-container" className="sticky bottom-0 p-4 sm:p-6 border-t rounded-2xl border-gray-200 bg-gradient-to-r from-gray-50/50 to-blue-50/30 flex justify-end gap-4">
                    <button
                        data-testid="cancel-button"
                        type="button"
                        onClick={handleClose}
                        className="px-4 py-2 bg-gradient-to-r from-gray-200 to-gray-300 hover:from-gray-300 hover:to-gray-400 text-gray-700 rounded-xl shadow-lg hover:shadow-xl transition-all duration-300 font-semibold text-sm hover:scale-105 cursor-pointer"
                    >
                        {t('addDoctors.cancel', 'Cancel')}
                    </button>
                    <button
                        data-testid="submit-button"
                        type="submit"
                        disabled={loading}
                        onClick={handleSubmit}
                        className="px-4 py-2 bg-gradient-to-r from-[#4ABDBD] via-[#3AA8A8] to-[#2D8A8A] text-white rounded-xl shadow-lg hover:shadow-xl hover:scale-105 transition-all duration-300 font-semibold text-sm cursor-pointer flex items-center gap-2"
                    >
                        {loading && (
                            <span data-testid="loading-spinner" className="border-2 border-white border-t-transparent rounded-full w-4 h-4 animate-spin"></span>
                        )}
                        {loading ? t('addDoctors.save') : t('addDoctors.create')}
                    </button>
                </div>
            </div>
        </div>
    );
}